import React, { useState, useEffect } from 'react';
import { Globe, MapPin, ArrowRight } from 'lucide-react';

/**
 * GeoGate — מציג הודעה למבקרים מחוץ לישראל
 * - לא חוסם בוטים של מנועי חיפוש (SEO safe)
 * - שומר בcache כדי לא לשאול כל פעם
 * - נותן אפשרות להמשיך בכל זאת (והבחירה נזכרת)
 * - חוסם רק על קוד מדינה תקין שאינו IL — כל תקלה או תשובה לא ברורה = לא חוסמים
 *
 * The country comes from our own /api/geo (functions/api/geo.js), i.e. from
 * Cloudflare. Until 2026-09-24 it came from ipapi.co, whose rate-limit reply
 * (HTTP 429, readable JSON without a country) was read as "not Israel" and
 * cached for 24 hours, so Israeli visitors could be locked out.
 */
const COUNTRY_CODE = /^[A-Z]{2}$/;
const CACHE_MS = 24 * 60 * 60 * 1000;

const readStorage = (key: string): string | null => {
  try { return localStorage.getItem(key); } catch { return null; }
};
const writeStorage = (key: string, value: string) => {
  try { localStorage.setItem(key, value); } catch { /* private mode etc. */ }
};

const GeoGate: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isBlocked, setIsBlocked] = useState(false);
  const [isChecking, setIsChecking] = useState(true);
  const [bypassed, setBypassed] = useState(() => readStorage('geo_bypassed') === 'true');

  useEffect(() => {
    // Don't block search engine bots
    const userAgent = navigator.userAgent.toLowerCase();
    const isBot = /googlebot|bingbot|yandex|baidu|duckduckbot|slurp|facebot|ia_archiver|crawl|spider|bot/i.test(userAgent);
    if (isBot || bypassed) {
      setIsChecking(false);
      return;
    }

    // Check cache first. Only a real country code counts: older versions
    // cached "undefined" (from the ipapi.co error) and blocked on it.
    const cached = readStorage('geo_country');
    const cacheTime = Number(readStorage('geo_cache_time'));
    if (cached && COUNTRY_CODE.test(cached) && cacheTime && Date.now() - cacheTime < CACHE_MS) {
      setIsBlocked(cached !== 'IL');
      setIsChecking(false);
      return;
    }

    fetch('/api/geo', { signal: AbortSignal.timeout(5000) })
      .then((response) => (response.ok ? response.json() : null))
      .then((data) => {
        const country = data?.country;
        if (typeof country === 'string' && COUNTRY_CODE.test(country)) {
          writeStorage('geo_country', country);
          writeStorage('geo_cache_time', Date.now().toString());
          setIsBlocked(country !== 'IL');
        }
        // Unknown country, an error reply, or no /api/geo (vite dev serves
        // index.html there) — none of these is a reason to block.
      })
      .catch(() => {})
      .finally(() => setIsChecking(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps -- runs once on mount
  }, []);

  // Show nothing while checking (prevents flash)
  if (isChecking) return <>{children}</>;

  // User bypassed the gate
  if (bypassed || !isBlocked) return <>{children}</>;

  // Show geo-block message
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 flex items-center justify-center p-4" dir="rtl">
      <div className="max-w-lg w-full bg-white/10 backdrop-blur-lg rounded-3xl p-8 md:p-12 text-center border border-white/20 shadow-2xl">
        {/* Icon */}
        <div className="inline-flex items-center justify-center w-20 h-20 bg-blue-500/20 rounded-full mb-6">
          <Globe className="w-10 h-10 text-blue-300" />
        </div>

        {/* Hebrew message */}
        <h1 className="text-3xl font-bold text-white mb-4">
          אתר זה מיועד ללקוחות בישראל
        </h1>
        <p className="text-blue-200 text-lg mb-2">
          בנה מחשבים — מעבדת מחשבים בנטור, רמת הגולן
        </p>
        <div className="flex items-center justify-center gap-2 text-blue-300 mb-8">
          <MapPin className="w-4 h-4" />
          <span>שירות לאזור רמת הגולן, קצרין, טבריה והצפון</span>
        </div>

        {/* English message */}
        <div className="bg-white/5 rounded-xl p-4 mb-8 text-left" dir="ltr">
          <p className="text-blue-200 text-sm">
            This website serves customers in Israel only.
            <br />
            BenaPC is a local computer repair shop in the Golan Heights, Israel.
          </p>
        </div>

        {/* Continue anyway button */}
        <button
          onClick={() => {
            setBypassed(true);
            writeStorage('geo_bypassed', 'true');
          }}
          className="text-blue-300 hover:text-white text-sm flex items-center gap-1 mx-auto transition-colors"
        >
          Continue anyway
          <ArrowRight className="w-3 h-3" />
        </button>
      </div>
    </div>
  );
};

export default GeoGate;
